import AuthenticatedLayout from '../components/AuthenticatedLayout'
import { Link } from '@inertiajs/react'

function StatCard({ label, value }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
    </div>
  )
}

function StoreRow({ store, showUser }) {
  return (
    <tr className="hover:bg-gray-50">
      {showUser && <td className="px-4 py-3 text-sm text-gray-900">{store.user_name}</td>}
      <td className="px-4 py-3 text-sm text-gray-900">{store.activity_name}</td>
      <td className="px-4 py-3 text-sm text-gray-500">{store.team_name}</td>
      <td className="px-4 py-3 text-sm text-gray-500">
        <Link href={`/stores/${store.id}/orders`} className="text-indigo-600 hover:underline">
          {store.orders_count} beställningar
        </Link>
      </td>
      <td className="px-4 py-3 text-sm font-medium text-gray-900">
        {formatCurrency(store.total_sales)}
      </td>
      <td className="px-4 py-3 text-sm">
        <a
          href={`/stores/${store.token}`}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-indigo-600 hover:underline"
        >
          Visa butik →
        </a>
      </td>
    </tr>
  )
}

function formatCurrency(amount) {
  return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount || 0)
}

function OnboardingChecklist({ stats }) {
  const hasTeams = stats.total_teams > 0
  const hasActivities = stats.total_activities > 0
  const allDone = hasTeams && hasActivities

  if (allDone) return null

  const steps = [
    {
      done: hasTeams,
      title: 'Skapa ett lag',
      description: 'Lägg till ett lag och bjud in medlemmar.',
      href: '/teams/new',
      linkLabel: 'Skapa lag →',
    },
    {
      done: hasActivities,
      title: 'Skapa en aktivitet',
      description: 'En aktivitet samlar produkter och lag under en försäljningskampanj.',
      href: '/activities/new',
      linkLabel: 'Skapa aktivitet →',
    },
  ]

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6">
      <h2 className="text-base font-semibold text-indigo-900 mb-1">Kom igång med Gosellgo</h2>
      <p className="text-sm text-indigo-700 mb-4">
        Följ stegen nedan för att sätta upp din förening.
      </p>
      <ol className="space-y-3">
        {steps.map((step, i) => (
          <li key={i} className="flex items-start gap-3">
            <span
              className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                step.done
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white border-2 border-indigo-300 text-indigo-400'
              }`}
            >
              {step.done ? '✓' : i + 1}
            </span>
            <div>
              <p className={`text-sm font-medium ${step.done ? 'text-indigo-400 line-through' : 'text-indigo-900'}`}>
                {step.title}
              </p>
              {!step.done && (
                <>
                  <p className="text-sm text-indigo-600">{step.description}</p>
                  <Link href={step.href} className="text-sm font-medium text-indigo-700 hover:underline">
                    {step.linkLabel}
                  </Link>
                </>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  )
}

export default function Dashboard({ role, stats, activities, stores, available_activities }) {
  return (
    <AuthenticatedLayout>
      <div className="space-y-8">
        <h1 className="text-2xl font-bold text-gray-900">Startsida</h1>

        {role === 'org_admin' && (
          <>
            <OnboardingChecklist stats={stats} />

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <StatCard label="Total försäljning" value={formatCurrency(stats.total_sales)} />
              <StatCard label="Totalt antal beställningar" value={stats.total_orders} />
              <StatCard label="Aktiva butiker" value={stats.total_stores} />
              <StatCard label="Aktiviteter" value={stats.total_activities} />
            </div>

            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Aktiviteter</h2>
                <Link href="/activities/new" className="text-sm text-indigo-600 hover:underline">
                  + Ny aktivitet
                </Link>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
                {activities?.length === 0 && (
                  <p className="px-4 py-8 text-center text-gray-500 text-sm">
                    Inga aktiviteter ännu.{' '}
                    <Link href="/activities/new" className="text-indigo-600 hover:underline">
                      Skapa en
                    </Link>
                  </p>
                )}
                {activities?.map(a => (
                  <Link
                    key={a.id}
                    href={`/activities/${a.id}`}
                    className="flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                  >
                    <span className="font-medium text-gray-900">{a.name}</span>
                    <span className="text-sm text-gray-500">
                      {a.products_count} produkter · {a.teams_count} lag
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </>
        )}

        {role === 'team_admin' && (
          <>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Lagförsäljning</h2>
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-left">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                    <tr>
                      <th className="px-4 py-3">Medlem</th>
                      <th className="px-4 py-3">Aktivitet</th>
                      <th className="px-4 py-3">Lag</th>
                      <th className="px-4 py-3">Beställningar</th>
                      <th className="px-4 py-3">Försäljning</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {stores?.map(s => <StoreRow key={s.id} store={s} showUser />)}
                    {stores?.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-gray-500 text-sm">
                          Inga butiker ännu.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {role === 'team_user' && (
          <>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Mina butiker</h2>
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-left">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                    <tr>
                      <th className="px-4 py-3">Aktivitet</th>
                      <th className="px-4 py-3">Lag</th>
                      <th className="px-4 py-3">Beställningar</th>
                      <th className="px-4 py-3">Försäljning</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {stores?.map(s => <StoreRow key={s.id} store={s} showUser={false} />)}
                    {stores?.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-gray-500 text-sm">
                          Inga butiker ännu. Gå med i en aktivitet nedan för att starta din försäljning.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {available_activities?.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Aktiviteter att gå med i</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  {available_activities.map((a, i) => (
                    <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{a.name}</p>
                        <p className="text-sm text-gray-500">{a.team_name}</p>
                      </div>
                      <Link
                        href={a.join_path}
                        className="inline-flex items-center px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700"
                      >
                        Gå med
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AuthenticatedLayout>
  )
}
